import { ContactFormValues } from '@pages/components/ContactForm';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { appConfig } from 'src/config';
import { Contact } from 'src/types/contact';
import { PaginatedResponse } from './types';
import { portalV2Api } from './portalV2';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';

export interface Tag {
  name: string;
}

export interface ImportFile {
  createdDate: string;
  fileName: string;
  id: number;
  importedCount: null;
  logPresent: boolean;
  status: 'UPLOADED';
  type: string;
}

export interface ImportFileAnalysis {
  delimiter: string;
  fileId: number;
  quote: string;
  sample: string[][];
}

export interface ImportContactsPayload {
  id: string;
  updateContactIfExist: boolean;
  tags: Tag[];
  columns: Record<string, number>;
}

export interface ValidatePhoneNumberResponse {
  internationalFormat: null | string;
  valid: boolean;
  type: null | 'MOBILE' | 'FIXED_LINE_OR_MOBILE';
}

export interface BulkTagsPayload {
  contactIds: number[];
  tagNames: string[];
}

export interface BulkTagsResponse {
  assignedContactIds: number[];
}

const normalizeTagsResponse = (response: unknown): Tag[] => {
  if (Array.isArray(response)) {
    return response
      .map((tag) => {
        if (typeof tag === 'string') {
          return { name: tag };
        }

        if (typeof tag === 'object' && tag !== null && 'name' in tag && typeof tag.name === 'string') {
          return { name: tag.name };
        }

        return null;
      })
      .filter((tag): tag is Tag => Boolean(tag));
  }

  if (typeof response === 'object' && response !== null) {
    const embedded = (response as { _embedded?: { contact_tag?: unknown[] } })._embedded;
    const contactTags = embedded?.contact_tag;
    if (Array.isArray(contactTags)) {
      return contactTags
        .map((tag) => (typeof tag === 'object' && tag !== null && 'name' in tag && typeof tag.name === 'string'
          ? { name: tag.name }
          : null))
        .filter((tag): tag is Tag => Boolean(tag));
    }
  }

  return [];
};

const normalizeTagNames = (tagNames: unknown): Tag[] => {
  if (!Array.isArray(tagNames)) {
    return [];
  }

  const uniqueTagNames = new Set<string>();

  tagNames.forEach((tagName) => {
    if (typeof tagName === 'string') {
      uniqueTagNames.add(tagName);
    }
  });

  return [...uniqueTagNames].sort().map((name) => ({ name }));
};

export const contactsApi = createApi({
  tagTypes: ['Tag', 'Contact', 'OptOut'],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.URL_REST}contacts/`,
    credentials: 'omit',
    prepareHeaders: (headers) => {
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json, application/hal+json');
      }
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json; charset=UTF-8');
      }
      return headers;
    },
  })),
  reducerPath: 'contactsApi',
  endpoints: (builder) => ({
    getTags: builder.query<Tag[], void>({
      async queryFn(_arg, api, _extraOptions, baseQuery) {
        // Use the same source as filters to keep tag lists consistent across all contact screens.
        const contactFiltersResult = await api.dispatch(
          portalV2Api.endpoints.getContactFilters.initiate(undefined, { subscribe: false, forceRefetch: true })
        );

        if (contactFiltersResult.data) {
          return { data: normalizeTagNames(contactFiltersResult.data.tags) };
        }

        const fallbackResult = await baseQuery({
          url: 'tags',
        });
        if (!fallbackResult.error) {
          return { data: normalizeTagsResponse(fallbackResult.data) };
        }

        return { error: fallbackResult.error ?? contactFiltersResult.error };
      },
      providesTags: [{ type: 'Tag', id: 'tags' }],
    }),
    getContact: builder.query<Contact, string>({
      query: (id) => ({
        url: id,
      }),
      providesTags: [{ type: 'Contact', id: 'contact' }],
    }),
    getContacts: builder.query<
      PaginatedResponse<'contacts', Contact>,
      { mobilePhone?: string; page: number; size: number; sort?: string; full?: boolean; 'contactTags.name'?: string[] }
    >({
      query: ({ 'contactTags.name': tags = [], ...params }) => {
        const searchParams = new URLSearchParams();

        tags.forEach((tag) => {
          if (tag) {
            searchParams.append('contactTags.name', tag);
          }
        });

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });

        const queryString = searchParams.toString();

        return {
          url: `${appConfig.URL_REST}contacts${queryString ? `?${queryString}` : ''}`,
        };
      },
      providesTags: [{ type: 'Contact', id: 'contacts' }],
    }),
    createContact: builder.mutation<Contact, Partial<ContactFormValues>>({
      query: (newContact) => ({
        url: '',
        method: 'POST',
        body: newContact,
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contact' },
        { type: 'Tag', id: 'tags' },
      ],
    }),
    updateContact: builder.mutation<Contact, ContactFormValues & { id: number }>({
      query: ({ id, ...updatedContact }) => ({
        url: id.toString(),
        method: 'PUT',
        body: updatedContact,
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contact' },
        { type: 'Tag', id: 'tags' },
      ],
    }),
    uploadImportFile: builder.query<ImportFile, FormData>({
      query: (formData) => ({
        url: 'files',
        method: 'POST',
        body: formData,
      }),
      keepUnusedDataFor: Infinity,
    }),
    analyzeImportFile: builder.query<ImportFileAnalysis, { fileId: number; delimiter: string; quote: string }>({
      query: ({ fileId, delimiter, quote }) => ({
        url: `files/${fileId}/analysis`,
        method: 'POST',
        body: { delimiter, quote },
      }),
      keepUnusedDataFor: Infinity,
    }),
    importContacts: builder.mutation<void, ImportContactsPayload>({
      query: ({ id, ...body }) => ({
        url: `files/${id}/import`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contact' },
        { type: 'Tag', id: 'tags' },
      ],
    }),
    deleteContact: builder.mutation<void, string>({
      query: (id) => ({
        url: id,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Contact', id: 'contact' },
        { type: 'Tag', id: 'tags' },
      ],
    }),
    validatePhoneNumber: builder.query<ValidatePhoneNumberResponse, FormData>({
      query: (body) => ({
        url: `check-phone-number`,
        method: 'POST',
        body,
      }),
      keepUnusedDataFor: Infinity,
    }),
    getContactsCount: builder.query<{ personalCount: number; totalCount: number; }, void>({
      query: () => ({
        url: 'count',
      }),
    }),
    checkOptOut: builder.query<boolean, string>({
      query: (mobilePhone) => ({
        url: `${appConfig.URL_REST}opt_out/${encodeURIComponent(mobilePhone)}/check`,
        method: 'GET',
      }),
      providesTags: (_res, _err, mobilePhone) => [{ type: 'OptOut', id: mobilePhone }],
    }),
    addOptOut: builder.mutation<void, string>({
      query: (mobilePhone) => ({
        url: `${appConfig.URL_REST}opt_out/${encodeURIComponent(mobilePhone)}/add`,
        method: 'POST',
      }),
      invalidatesTags: (_res, _err, mobilePhone) => [
        { type: 'OptOut', id: mobilePhone },
        { type: 'Contact', id: 'contact' },
      ],
    }),
    removeOptOut: builder.mutation<void, string>({
      query: (mobilePhone) => ({
        url: `${appConfig.URL_REST}opt_out/${encodeURIComponent(mobilePhone)}/remove`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, mobilePhone) => [
        { type: 'OptOut', id: mobilePhone },
        { type: 'Contact', id: 'contact' },
      ],
    }),
  }),
});

export const {
  useGetTagsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useLazyUploadImportFileQuery,
  useLazyAnalyzeImportFileQuery,
  useImportContactsMutation,
  useDeleteContactMutation,
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useLazyValidatePhoneNumberQuery,
  useGetContactsCountQuery,
  useCheckOptOutQuery,
  useAddOptOutMutation,
  useRemoveOptOutMutation,
} = contactsApi;
